import { useToasts } from '../../components/toasts/useToasts';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { useClientPortalActions } from '../../redux/actions/clientPortalActions';
import { useClientPortalState } from '../../redux/reducers/clientPortalReducer';
import { useApi } from '../api/useApi';
import { ClientPortal, ClientPortalUser, UserAccount } from '../types';

export const ClientPortalApiRoutes = {
    getClientPortal: (portalId: string) => `portal/${portalId}`,
    getClientPortalsForUser: (accountId: string, userId: string) => `account/${accountId}/user/${userId}`,
    upsertClientPortal: () => 'portal/upsert',
    deleteClientPortal: (portalId: string) => `portal/${portalId}`,
    getClientPortalUsers: (portalId: string) => `portal/${portalId}/users`,
    getClientPortalUser: (portalId: string, userId: string) => `portal/${portalId}/user/${userId}`,
    upsertClientPortalUser: (portalId: string) => `portal/${portalId}/user`,
    deleteClientPortalUser: (portalId: string, userId: string) => `portal/${portalId}/user/${userId}`,
};

export type ClientPortalService = {
    clientPortals: ClientPortal[];
    clientPortalUsers: ClientPortalUser[];
    getClientPortalAsync: () => Promise<ClientPortal | undefined>;
    getClientPortalsForUserAsync: (userId: string) => Promise<ClientPortal[]>;
    upsertClientPortalAsync: (portal: ClientPortal) => Promise<ClientPortal>;
    deleteClientPortalAsync: () => Promise<void>;
    getClientPortalUsersAsync: () => Promise<ClientPortalUser[]>;
    getClientPortalUserAsync: (userId: string, portalIdOverride?: string) => Promise<ClientPortalUser | undefined>;
    upsertClientPortalUserAsync: (user: ClientPortalUser) => Promise<ClientPortalUser>;
    deleteClientPortalUserAsync: (userId: string) => Promise<void>;
    clearPortals: () => void;
};

export const useClientPortalService = (caseId?: string): ClientPortalService => {
    const { primaryAccount } = useAccountState();
    const { account } = (primaryAccount as UserAccount) ?? {};
    const clientPortalState = useClientPortalState();
    const clientPortalActions = useClientPortalActions();
    const toasts = useToasts();

    const api = useApi<ClientPortal | ClientPortalUser>('api/ClientPortal');

    const getClientPortalAsync = async (): Promise<ClientPortal | undefined> => {
        const portalId = caseId as string;
        const portal = await api.getAsync(ClientPortalApiRoutes.getClientPortal(portalId));
        const clientPortal = portal as ClientPortal | undefined;
        if (clientPortal) {
            clientPortalActions.addOrReplaceClientPortal(clientPortal);
        } else {
            clientPortalActions.removeClientPortal(portalId);
        }
        return clientPortal;
    };

    const getClientPortalsForUserAsync = async (userId: string): Promise<ClientPortal[]> => {
        const portals = await api.getAsync(
            ClientPortalApiRoutes.getClientPortalsForUser(account?.accountId as string, userId),
        );
        const clientPortals = (portals ?? []) as ClientPortal[];
        clientPortalActions.setClientPortals(clientPortals);
        return clientPortals;
    };

    const upsertClientPortalAsync = async (portal: ClientPortal): Promise<ClientPortal> => {
        clientPortalActions.addOrReplaceClientPortal(portal);
        const updatedPortal = await api.postAsync(portal, ClientPortalApiRoutes.upsertClientPortal());
        clientPortalActions.addOrReplaceClientPortal(updatedPortal as ClientPortal);
        return updatedPortal as ClientPortal;
    };

    const deleteClientPortalAsync = async (): Promise<void> => {
        await api.deleteAsync([], ClientPortalApiRoutes.deleteClientPortal(caseId as string));
        clientPortalActions.removeClientPortal(caseId as string);
        clientPortalActions.setClientPortalUsers([]);
    };

    const clearPortals = (): void => {
        clientPortalActions.setClientPortals([]);
        clientPortalActions.setClientPortalUsers([]);
    };

    const getClientPortalUsersAsync = async (): Promise<ClientPortalUser[]> => {
        const users = await api.getAsync(
            ClientPortalApiRoutes.getClientPortalUsers(caseId as string),
        );
        const clientPortalUsers = (users ?? []) as ClientPortalUser[];
        clientPortalActions.setClientPortalUsers(clientPortalUsers);
        return clientPortalUsers;
    };

    const getClientPortalUserAsync = async (
        userId: string,
        portalIdOverride?: string,
    ): Promise<ClientPortalUser | undefined> => {
        const portalId = portalIdOverride ?? caseId as string;
        const user = (await api.getAsync(
            ClientPortalApiRoutes.getClientPortalUser(portalId, userId),
        )) as ClientPortalUser | undefined;
        if (user) {
            clientPortalActions.addOrReplaceClientPortalUser(user);
        }
        return user;
    };

    const upsertClientPortalUserAsync = async (user: ClientPortalUser): Promise<ClientPortalUser> => {
        clientPortalActions.addOrReplaceClientPortalUser(user);
        const updatedUser: ClientPortalUser = await api.postAsync(
            user,
            ClientPortalApiRoutes.upsertClientPortalUser(user.portalId ?? caseId as string),
        );
        clientPortalActions.addOrReplaceClientPortalUser(updatedUser);
        toasts.notifySuccess('Client Portal User Added', 'The user has been added to the client portal.');
        return updatedUser;
    };

    const deleteClientPortalUserAsync = async (userId: string): Promise<void> => {
        clientPortalActions.removeClientPortalUser(userId);
        await api.deleteAsync(
            [userId],
            ClientPortalApiRoutes.deleteClientPortalUser(caseId as string, userId),
        );
        toasts.notifySuccess('Client Portal User Deleted', 'The user has been deleted from the client portal.');
    };

    return {
        clientPortals: clientPortalState.portals,
        clientPortalUsers: clientPortalState.users,
        getClientPortalAsync,
        getClientPortalsForUserAsync,
        upsertClientPortalAsync,
        deleteClientPortalAsync,
        getClientPortalUsersAsync,
        getClientPortalUserAsync,
        upsertClientPortalUserAsync,
        deleteClientPortalUserAsync,
        clearPortals,
    };
};
